import UniversalRouter from 'universal-router';
import generateUrls from 'universal-router/generate-urls';

/**
 * @typedef {{[key: string]: any}} RouterState
 * @typedef {{
 *   routeName: string,
 *   getState: () => RouterState,
 *   setState: (state: RouterState) => void,
 *   getDefaultState: () => RouterState,
 * }} RouterIntegrationOptions
 */

function stateMatches(a, b) {
    return JSON.stringify(a, Object.keys(a).sort()) === JSON.stringify(b, Object.keys(b).sort());
}

/**
 * A wrapper around UniversalRouter which adds history integration
 */
export class Router {
    /**
     * @param {import('universal-router').Routes<RouterState>} routes The routes passed to UniversalRouter
     * @param {RouterIntegrationOptions} options Options
     * @param {import('universal-router').RouterOptions<RouterState>} unioptions Options passed to UniversalRouter
     */
    constructor(routes, options, unioptions) {
        this.getState = options.getState;
        this.setState = options.setState;
        this.getDefaultState = options.getDefaultState;
        // XXX: We only have one route atm
        // If we need more we need to pass the route name to each function
        this.routeName = options.routeName;

        console.assert(Boolean(this.getState));
        console.assert(Boolean(this.setState));
        console.assert(Boolean(this.getDefaultState));
        console.assert(Boolean(this.routeName));

        // https://github.com/kriasoft/universal-router
        this.router = new UniversalRouter(routes, unioptions);

        window.addEventListener('popstate', (event) => {
            void this.setStateFromCurrentLocation();
            this._dispatchLocationChanged();
        });
    }

    async _getStateForPath(pathname) {
        let isBasePath = pathname.replace(/\/$/, '') === this.router.baseUrl.replace(/\/$/, '');
        if (isBasePath) {
            return this.getDefaultState();
        }
        const state = await this.router.resolve({pathname: pathname});
        if (state == null) {
            throw new Error(`No route found for ${pathname}`);
        }
        return state;
    }

    /**
     * In case something else has changed the location, update the app state accordingly.
     */
    async setStateFromCurrentLocation() {
        let page;
        try {
            page = await this._getStateForPath(location.pathname);
        } catch {
            // In case we can't resolve the location, just leave things as is.
            // This happens when a user enters a wrong URL or when testing with karma.
            return;
        }

        this.setState(page);
    }

    /**
     * Update the router after some internal state change.
     */
    async update() {
        // Queue updates so we can call this multiple times when changing state
        // without it resulting in multiple location changes
        await new Promise((resolve) => setTimeout(resolve));

        let page;
        try {
            page = await this._getStateForPath(location.pathname);
        } catch {
            // In case we can't resolve the location, just leave things as is.
            // This happens when a user enters a wrong URL or when testing with karma.
            return;
        }

        const newState = this.getState();

        // if the state has changed we update
        if (!stateMatches(newState, page)) {
            const newPathname = this.getPathname();
            const referrerUrl = location.href;
            window.history.pushState({}, '', newPathname);
            this._dispatchLocationChanged(referrerUrl);
        }
    }

    /**
     * Given a new routing URL set the location and the app state.
     *
     * @param {string} relUrl
     */
    async updateFromUrl(relUrl) {
        let url = new URL(relUrl, location.href);
        let pathname = url.pathname;

        let page;
        try {
            page = await this._getStateForPath(pathname);
        } catch {
            // In case we can't resolve the location, just leave things as is.
            // This happens when a user enters a wrong URL or when testing with karma.
            return;
        }

        const oldState = this.getState();

        const newUrl = pathname + url.search + url.hash;
        const currentUrl = location.pathname + location.search + location.hash;

        if (!stateMatches(oldState, page) || currentUrl !== newUrl) {
            const referrerUrl = location.href;
            window.history.pushState({}, '', newUrl);
            if (!stateMatches(oldState, page)) {
                this.setState(page);
            }
            this._dispatchLocationChanged(referrerUrl);
        }
    }

    /**
     * Pass some new router state to get a new router path that can
     * be passed to updateFromUrl() later on. If nothing is
     * passed the current state is used.
     *
     * @param {object} [partialState] The optional partial new state
     * @returns {string} The new path
     */
    getPathname(partialState) {
        const currentState = this.getState();
        if (partialState === undefined) {
            partialState = {};
        }
        let combined = {...currentState, ...partialState};

        try {
            return generateUrls(this.router)(this.routeName, combined);
        } catch {
            // XXX: In case we have partial state this will fail, just return the old path
            return location.pathname;
        }
    }

    _dispatchLocationChanged(referrerUrl = '') {
        // fire a locationchanged event
        window.dispatchEvent(
            new CustomEvent('locationchanged', {
                detail: {
                    referrerUrl: referrerUrl,
                },
                bubbles: true,
            }),
        );
    }
}
